import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import CongregationStudentBoardTable from '@/tables/CongregationStudentBoardTable';
import { useCoordinatorAccess } from '~/hooks/useIsCoordinator';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/student-board/',
)({
  component: StudentBoardRoute,
});

/**
 * The group's Student Board tab, for admins and mods of congregations and
 * custom study groups, the same rule the server runs for `group_id`. Legacy
 * hides the tab rather than returning 403, so a refused caller is redirected to
 * the group dashboard.
 *
 * No platform-admin arm: the board endpoint requires a study helper, so an
 * administrator would see a permanently empty board.
 *
 * `isResolved` matters: `getGroupBySlug` is pending on first paint, and a bare
 * `!isCoordinator` would bounce the group's own admin.
 */
function StudentBoardRoute(): ReactElement | null {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  const navigate = useNavigate();
  const { isCoordinator, isResolved } = useCoordinatorAccess();
  const refused = isResolved && !isCoordinator;

  useEffect(() => {
    if (refused) {
      navigate({
        to: '/groups/$groupName/',
        params: { groupName },
        replace: true,
      });
    }
  }, [refused, groupName, navigate]);

  if (refused) return null;

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('lessons.student-board')}
          placement="group_student_board"
        />
      </GroupHeaderWrapper>
      <CongregationStudentBoardTable />
    </>
  );
}
